/**
 * The load simulation — admin only, and it does real work on the server.
 *
 * `POST /sym/sim` runs one screener build against N concurrent chart readers and
 * reports both, plus the ratio between the chart reads alone and the same reads
 * with the build underneath. That ratio is the point: a screener that is slow is
 * one problem, a screener that makes every chart on the page slow is a different
 * one, and only the comparison distinguishes them.
 *
 * Throws on failure like `api/journal.ts` rather than resolving quietly — this
 * is a deliberate action with a button behind it, and a silent no-op would read
 * as "the simulation says everything is fine".
 */

import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = (
  httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || ""
).replace(/\/$/, "");

export interface SimStage {
  name: string;
  ms: number;
  detail?: string;
}

export interface SimResult {
  market: string;
  group: string;
  chartUsers: number;
  shiftDays: number;
  cold: boolean;
  screener: {
    group: string;
    ms: number;
    payloadBytes: number;
    mergedRows: number;
    stages: SimStage[];
    error?: string;
  };
  chart: {
    baseline: { min: number; median: number; p90: number; max: number };
    loaded: { min: number; median: number; p90: number; max: number };
    /** loaded ÷ baseline. Above 1 means the build slowed every chart down. */
    impactFactor: number | null;
    slowest: { symbol: string; ms: number; rows: number }[];
    errors: { symbol: string; error?: string }[];
  };
  error?: string;
}

export interface SimOptions {
  group?: string;
  chartUsers?: number;
  shiftDays?: number;
  market?: string;
  /** Drop the base cache first — the deploy-shaped case. Costs a real rebuild. */
  cold?: boolean;
}

export async function runSimulation(options: SimOptions = {}): Promise<SimResult> {
  if (!BASE) throw new Error("no server configured");
  const query = new URLSearchParams({ userId: currentUserId() });
  if (options.group) query.set("group", options.group);
  if (options.chartUsers != null) query.set("chartUsers", String(options.chartUsers));
  if (options.shiftDays != null) query.set("shiftDays", String(options.shiftDays));
  if (options.market) query.set("market", options.market);
  if (options.cold) query.set("cold", "true");

  const response = await fetch(`${BASE}/sym/sim?${query}`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(await requestHeaders()) },
  });
  if (!response.ok) {
    // 403 is the ordinary answer for anyone who is not an admin, and it is
    // worth showing verbatim rather than as "simulation failed".
    const detail = await response.text().catch(() => "");
    throw new Error(detail || `simulation ${response.status}`);
  }
  return (await response.json()) as SimResult;
}
